import cx from "classnames";
import { useRef } from "react";

import { Button, Flex, Table, Text } from "@theorgchartdev/storybook";
import { CurrentPlanFieldsModal } from "@/appComponents/Blocks/Modals/PlanFieldsModal";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { appstr } from "@coreapp/utils/appstr";
import { AccordionSections } from "@/appComponents/Blocks/Modals/PlanFieldsModal/AccordionList";
import { enumObjClassNames, enumObjectTypes } from "@coreapp/constants";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { GetFieldNameLocalizationMap, GetPlanFieldsLocalizationMap } from "@/Utils/DataLib";
import { BuildFieldOptions } from "@/appComponents/Blocks/Modals/PlanFieldsModal/BuildFieldOptions";

export const FieldsTable = props => {
  const { planMgr } = props;
  const fieldsHandleRef = useRef(null);

  const doc = planMgr?.GetDoc();

  const { canManageFields } = getPlanPermissions({ useCurrentPlan: true });

  const isPositionPlan = planMgr?.IsPositionPlan();
  const planInfo = planMgr?.GetPlanInfo();

  const holderPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.box, isPositionPlan) ?? {},
    enumObjClassNames.box,
    doc
  );
  const positionPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.position, isPositionPlan) ?? {},
    enumObjClassNames.position,
    doc
  );

  const getFieldOptionLabel = (fieldName, fieldType, localizationMap) => {
    const fieldLabel = doc
      ?.GetObjDB()
      ?.GetClassByName(
        fieldType === "position" ? enumObjClassNames.position : enumObjClassNames.box
      )
      ?.GetFieldLabelFromName(fieldName);
    const label = localizationMap[fieldName] ?? fieldLabel ?? fieldName;
    return label;
  };

  const BuildPosPlanFieldOptions = (fieldsList, fieldType = "holder", localizationMap = {}) => {
    const actionListItemPropsCallback = () => ({
      labelProps: {
        size: "xsmall"
      }
    });
    return BuildFieldOptions(
      fieldsList,
      isPositionPlan,
      fieldType,
      localizationMap,
      actionListItemPropsCallback
    );
  };

  const BuildRegularPlanFieldOptions = (fieldsList, fieldType = "holder", localizationMap = {}) => {
    return fieldsList.map(fieldName => {
      const label = getFieldOptionLabel(fieldName, fieldType, localizationMap);
      return {
        name: label
      };
    });
  };

  const additionalFields = planInfo?.fields;

  const holderAdditionalFields = additionalFields?.holder ?? [];
  const positionAdditionalFields = additionalFields?.position ?? [];
  const tableData = isPositionPlan
    ? []
    : BuildRegularPlanFieldOptions(holderAdditionalFields, "holder", holderPlanLocalizationMap);

  return (
    <>
      <Flex direction={"column"}>
        <Flex className={"oc-dashboard-header"}>
          <Flex gap={"2px"} alignItems={"flex-end"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("Fields")}
            </Text>
          </Flex>
          {canManageFields && (
            <span>
              <Button
                dataTestId={"fields-table-manage-fields"}
                onClick={() => {
                  fieldsHandleRef.current?.handleOpen();
                }}
                modifier={"secondary"}
                size={"small"}
              >
                {appstr("Manage plan fields")}
              </Button>
            </span>
          )}
        </Flex>
        <div data-testid="fields-table" className={cx("oc-metrics-table", "ocn-list-scrollbar")}>
          {isPositionPlan ? (
            <Flex
              direction={"column"}
              gap={"6px"}
              padding={"8px"}
              alignItems="flex-start"
              justifyContent="center"
              width={"100%"}
            >
              <AccordionSections
                sections={[
                  {
                    name: appstr("Employee fields"),
                    data: holderAdditionalFields,
                    objType: enumObjectTypes.holder,
                    options: BuildPosPlanFieldOptions(
                      holderAdditionalFields,
                      "holder",
                      holderPlanLocalizationMap
                    ),
                    dataTestId: "fields-table-employee-fields"
                  },
                  {
                    name: appstr("Position fields"),
                    data: positionAdditionalFields,
                    objType: enumObjectTypes.position,
                    options: BuildPosPlanFieldOptions(
                      positionAdditionalFields,
                      "position",
                      positionPlanLocalizationMap
                    ),
                    dataTestId: "fields-table-position-fields"
                  }
                ]}
              />
            </Flex>
          ) : (
            <Table
              gridOptions={{
                getRowStyle: (_data, _index, isHovered) => {
                  if (isHovered && canManageFields) {
                    return { cursor: "pointer", background: "#ececed" };
                  }
                }
              }}
              columns={[
                {
                  field: "name",
                  headerName: appstr("Name"),
                  columnOptions: {
                    columnStyle: { color: "#3d3d4a" }
                  }
                }
              ]}
              data={tableData}
              showHeader={true}
              containerPadding={false}
              sortEnabled={true}
              rowSize={"mediumR"}
              textSize={"smallT"}
              customHeaderClass={"oc-table-header-display-small"}
              customTableClass={"oc-table-display-small"}
            />
          )}
        </div>
      </Flex>
      <CurrentPlanFieldsModal handleRef={fieldsHandleRef} planMgr={planMgr} />
    </>
  );
};
